import React, { useState, useReducer, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Radio,
} from "antd";
import moment from "moment";
import { useParams } from "react-router-dom";

import {
  FETCH_ALL_ARTICLE_API,
  FETCH_ALL_COLOR_API,
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_SHIPPING_API,
  FETCH_ALL_STOCK_API,
} from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";

import { updateShipping } from "../../../Services/ShippingService";
import { updateStock, createStock, deleteStock, patchIntersectStocks } from "../../../Services/StockService";
import StockCreationForm from "../../elements/Forms/StockCreationForm/StockCreationForm";

const ShippingDetailPage = ({ history, userData }) => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();

  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [shippingId, setShippingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consignmentType, setConsignmentType] = useState("putus");

  const [selectedNoSJ, setSelectedNoSJ] = useState(null); // <-- used to fetch stocks

  const initialState = { searchKeyword: "", filteredInfo: {}, sorter: {} };
  const initialStateStock = { searchKeyword: "", filteredInfo: {}, sorter: {}, current : 1, pageSize: 1000 };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [stateStock, dispatchStock] = useReducer(reducer, initialStateStock);

  // counters
  const { fetchedData: countersFetched = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    stateStock
  );
  const counterData = countersFetched?.data || [];

  // articles (safe defaults)
  const { 
    fetchedData: articleFetched = {}, 
    loading: loadingArticles,
    error: articleError 
  } = useFetch(
    `${FETCH_ALL_ARTICLE_API}`,
    stateStock
  );
  const ArticleData = Array.isArray(articleFetched?.data?.data) ? articleFetched?.data?.data : [];
  
  console.log(ArticleData);
  // Debug logging for ArticleData
  useEffect(() => {
    console.log("=== ArticleData Debug ===");
    console.log("articleFetched:", articleFetched);
    console.log("articleFetched?.data:", articleFetched?.data);
    console.log("articleFetched?.data?.data:", articleFetched?.data?.data);


    console.log("ArticleData:", ArticleData);
    console.log("ArticleData is array:", Array.isArray(ArticleData));
    console.log("loadingArticles:", loadingArticles);
    console.log("articleError:", articleError);
    
    if (articleError) {
      console.error("Error fetching articles:", articleError);
    }
  }, [articleFetched, ArticleData, loadingArticles, articleError]);

  // colors (safe defaults)
  const { fetchedData: colorFetched = {} } = useFetch(
    `${FETCH_ALL_COLOR_API}`,
    stateStock
  );
  const ColorData = colorFetched?.data || [];

  // stocks for selected noSJ (hook must be top-level, not inside effects)
  const {
    fetchedData: stockFetched = {},
    callReFetch: callReFetchStock,
    loading: loadingStock,
  } = useFetch(
    selectedNoSJ ? `${FETCH_ALL_STOCK_API}/sj/${selectedNoSJ}` : null,
    stateStock
  );

  const [originalShipping, setOriginalShipping] = useState(null);
  const getItemKey = (it) =>
    it?._id ||
    `${it?.barcode || ""}-${it?.article || ""}-${it?.color || ""}-${
      it?.size || ""
    }`;

  const shallowEqual = (a, b, fields) =>
    fields.every((f) => {
      const av = a?.[f];
      const bv = b?.[f];
      return av === bv || (av == null && bv == null);
    });

  // fields
  const INTERSECT_FIELDS = ["noSJ", "destinationCode", "status"];
  const SHIPPING_NON_INTERSECT_FIELDS = [
    "noForm",
    "sender",
    "destination",
    "destinationCode", // destinationCode also used in intersect; still safe
    "keterangan",
    "consignmentType",
    "tanggal_kirim",
    "tanggal_terima_eta",
  ];
  const ITEM_FIELDS_TO_COMPARE = [
    "article",
    "color",
    "size",
    "quantity",
    "assort",
    "price",
    "harga_internal",
    "barcode",
    "currentQuantity",
  ];
// put this helper ABOVE fetchShippingData so you can use it there

  // pick up shippingId from params
  useEffect(() => {
    if (id) setShippingId(id);
  }, [id]);

  const normalizeDate = (v) => {
    if (!v) return null;
    if (typeof v?.toISOString === "function") return v.toISOString();
    if (typeof v?.toDate === "function") return v.toDate().toISOString();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  // fetch shipping detail (plain fetch is fine)
  useEffect(() => {

    const fetchShippingData = async () => {
      if (!shippingId) return;

      try {
        setLoading(true);
        const resp = await fetch(`${FETCH_ALL_SHIPPING_API}/detail/${shippingId}`);
        const raw = await resp.json();
        const shippingData = raw?.data ?? raw; // support both {data:{}} or {}

        if (shippingData) {
          // auto-fill form; keep noSJ as string to preserve any leading zeros
          form.setFieldsValue({
            noForm: shippingData.noForm ?? "",
            noSJ: (shippingData.noSJ ?? "").toString(),
            sender: shippingData.sender,
            destination: shippingData.destination,
            destinationCode: shippingData.destinationCode,
            keterangan: shippingData.keterangan,
            totalCost: shippingData.totalCost,
            totalQuantity: shippingData.totalQuantity,
            tanggal_kirim: shippingData.tanggal_kirim ? moment(shippingData.tanggal_kirim) : null,
            tanggal_terima_eta: shippingData.tanggal_terima_eta ? moment(shippingData.tanggal_terima_eta) : null,
          });

          setOriginalShipping({
            noForm: shippingData.noForm ?? "",
            noSJ: (shippingData.noSJ ?? "").toString(),
            sender: shippingData.sender ?? "",
            destination: shippingData.destination ?? "",
            destinationCode: shippingData.destinationCode ?? "",
            keterangan: shippingData.keterangan ?? "",
            consignmentType: shippingData.consignmentType ?? "putus",
            status: shippingData.status ?? "in delivery",
            tanggal_kirim: normalizeDate(shippingData.tanggal_kirim),
            tanggal_terima_eta: normalizeDate(shippingData.tanggal_terima_eta),
            totalCost: shippingData.totalCost ?? 0,
            totalQuantity: shippingData.totalQuantity ?? 0,
          });
          

          setConsignmentType(shippingData.consignmentType ?? "putus");


          // trigger stock fetch via top-level useFetch
          setSelectedNoSJ((shippingData.noSJ ?? "").toString());
        }
      } catch (err) {
        console.error("Error fetching shipping data:", err);
        message.error("Failed to load shipping data");
      } finally {
        setLoading(false);
      }
    };

    fetchShippingData();
  }, [shippingId, form]);

  // refetch stocks whenever noSJ changes
  useEffect(() => {
    if (selectedNoSJ) callReFetchStock?.();
  }, [selectedNoSJ]);

  // build items when stockFetched changes
  useEffect(() => {
    // support shapes: array OR {data: array}
    const raw = stockFetched?.data ?? stockFetched;
    const stockList = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

    if (stockList.length > 0) {
      const formatted = stockList.map((item) => ({
        ...item,
        inputQuantity: item.quantity / (item.assort ? parseInt(item.assort) : 1),
      }));
      setItems(formatted);
      setOriginalItems(formatted);
    }
  }, [stockFetched]);

  // recompute totals (only non-deleted items)
  useEffect(() => {
    const activeItems = items.filter((it) => !it._deleted);
    const totalQuantity = activeItems.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
    const totalCost = activeItems.reduce(
      (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
      0
    );
    form.setFieldsValue({ totalQuantity, totalCost });
  }, [items, form]);

  // ----- add/edit item handlers -----
  const handleAddOrEditItem = async () => {
    try {
      const values = await modalForm.validateFields();
      
      // Ensure ArticleData is an array before using .find()
      if (!Array.isArray(ArticleData)) {
        console.error("ArticleData is not an array:", ArticleData);
        message.error("Article data is not available. Please try again.");
        return;
      }
      
      const selectedArticle = ArticleData.find((a) => a.code === values.article);

      // range sizes: "12 14" or "1214"
      if (values.size && (values.size.includes(" ") || values.size.length > 2)) {
        let sizes = [];
        if (values.size.includes(" ")) {
          const [start, end] = values.size.split(" ").map(Number);
          const count = end - start + 1;
          sizes = Array.from({ length: count }, (_, i) => start + i);
        } else {
          const s = values.size.toString();
          const start = parseInt(s.substring(0, 2));
          const end = parseInt(s.substring(s.length - 2));
          const count = end - start + 1;
          sizes = Array.from({ length: count }, (_, i) => start + i);
        }

        const newItems = sizes
          .map((sz, i) => {
            const currentSize = sz.toString().padStart(2, "0");
            const assortValue = values.assort && values.assort[i] ? parseInt(values.assort[i]) : 1;
            const calculatedQuantity = assortValue * Number(values.quantity || 0);
            return {
              ...values,
              size: currentSize,
              price: selectedArticle?.hpp || values.price,
              harga_internal: selectedArticle?.harga_internal || values.harga_internal,
              assort: values.assort && values.assort[i] ? values.assort[i] : "",
              inputQuantity: Number(values.quantity || 0),
              quantity: calculatedQuantity,
              barcode: `${values.barcode.slice(0, -2)}${currentSize}`,
            };
          })
          .filter((_, i) => values.assort && values.assort[i] !== "0");

        setItems((prev) => {
          if (editingItemIndex === null) return [...prev, ...newItems];
          const updated = [...prev];
          updated.splice(editingItemIndex, 1, ...newItems);
          return updated;
        });
      } else {
        // single size
        const assortValue = values.assort ? parseInt(values.assort) : 1;
        const calculatedQuantity =
          editingItemIndex !== null && values.currentQuantity
            ? Number(values.currentQuantity)
            : assortValue * Number(values.quantity || 0);

        const item = {
          ...values,
          price: selectedArticle?.hpp || values.price,
          harga_internal: selectedArticle?.harga_internal || values.harga_internal,
          inputQuantity: calculatedQuantity / (assortValue || 1),
          quantity: calculatedQuantity,
        };

        setItems((prev) => {
          if (editingItemIndex === null) return [...prev, item];
          const updated = [...prev];
          updated[editingItemIndex] = item;
          return updated;
        });
      }

      modalForm.resetFields();
      setIsModalVisible(false);
      setEditingItemIndex(null);
    } catch (e) {
      console.error("Validation Failed:", e);
    }
  };

  const handleEditItem = (record) => {
    // Check if ArticleData is available before opening modal
    if (!Array.isArray(ArticleData) || ArticleData.length === 0) {
      message.error("Article data is not available. Please try again later.");
      return;
    }
    
    const idx = items.findIndex((it) => getItemKey(it) === getItemKey(record));
    if (idx === -1) return;
    setEditingItemIndex(idx);
    modalForm.setFieldsValue(items[idx]);
    setIsModalVisible(true);
  };

  const handleDeleteItem = (record) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => getItemKey(it) === getItemKey(record));
      if (idx === -1) return prev;
      const updated = [...prev];
      const it = updated[idx];
      if (it._id) {
        updated[idx] = { ...it, _deleted: true };
        message.success("Item marked for deletion");
        return updated;
      }
      return prev.filter((_, i) => i !== idx);
    });
  };
  

  const handleAddItem = () => {
    // Check if ArticleData is available before opening modal
    if (!Array.isArray(ArticleData) || ArticleData.length === 0) {
      message.error("Article data is not available. Please try again later.");
      return;
    }
    
    setEditingItemIndex(null);
    modalForm.resetFields();
    setIsModalVisible(true);
  };





  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const activeItems = items.filter((it) => !it._deleted);
      if (activeItems.length === 0) {
        message.error("You must have at least one active item!");
        return;
      }

      // derive totals from active items
      const totalQuantity = activeItems.reduce(
        (s, it) => s + Number(it.quantity || 0),
        0
      );
      const totalCost = activeItems.reduce(
        (s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0),
        0
      );

      const destinationCode = String(
        values.destinationCode || values.destination || ""
      ).trim();
      if (!destinationCode) {
        message.error("Please select a destination counter.");
        return;
      }

      // normalized shipping payload
      const newShipping = {
        noForm: values.noForm ?? "",
        noSJ: (values.noSJ ?? "").toString(),
        sender: values.sender ?? "",
        destination: destinationCode,
        destinationCode,
        keterangan: values.keterangan ?? "",
        consignmentType,
        status: "in delivery",
        tanggal_kirim: normalizeDate(values.tanggal_kirim),
        tanggal_terima_eta: normalizeDate(values.tanggal_terima_eta),
        totalCost,
        totalQuantity,
      };

      // compare shipping
      const intersectChanged =
        !originalShipping ||
        !shallowEqual(newShipping, originalShipping, INTERSECT_FIELDS);

      const shippingOtherChanged =
        !originalShipping ||
        !shallowEqual(
          newShipping,
          originalShipping,
          SHIPPING_NON_INTERSECT_FIELDS
        );

      // compute stock diffs vs original
      const originalActive = (originalItems || []).filter((it) => !it._deleted);
      const originalMap = new Map(
        originalActive.map((it) => [getItemKey(it), it])
      );
      const currentMap = new Map(activeItems.map((it) => [getItemKey(it), it]));

      const added = [];
      const modified = [];
      const deletedMissing = [];

      for (const [k, cur] of currentMap.entries()) {
        const prev = originalMap.get(k);
        if (!prev) added.push(cur);
        else if (!shallowEqual(cur, prev, ITEM_FIELDS_TO_COMPARE))
          modified.push({ prev, cur });
      }
      for (const [k, prev] of originalMap.entries()) {
        if (!currentMap.has(k) && prev?._id) deletedMissing.push(prev);
      }
      const toDeleteFlagged = items.filter((it) => it._deleted && it._id);

      const stockIntrinsicChanged =
        added.length > 0 ||
        modified.length > 0 ||
        toDeleteFlagged.length > 0 ||
        deletedMissing.length > 0;

      // common builder for per-row payloads (uses new intersect values)
      const buildPayload = (it) => ({
        noSJ: newShipping.noSJ,
        article: it.article || "",
        color: it.color || "",
        size: Number(it.size) || 0,
        quantity: Number(it.quantity) || 0,
        assort: it.assort || "",
        price: Number(it.price) || 0,
        harga_internal: Number(it.harga_internal) || 0,
        barcode: it.barcode,
        currentQuantity: Number(it.currentQuantity) || 0,
        counterCode: newShipping.destinationCode,
        status: newShipping.status,
        _id: it._id,
      });

      // ===== Branches =====

      // A) Only shipping (non-intersect) changed -> update shipping only
      if (shippingOtherChanged && !intersectChanged && !stockIntrinsicChanged) {
        await updateShipping(shippingId, newShipping);
        message.success("Shipping updated.");
        history.push("/user/shipping");
        return;
      }

      // B) Only intersect changed -> updateMany stocks + update shipping
      if (intersectChanged && !stockIntrinsicChanged) {
        await patchIntersectStocks(
          {
            noSJ: originalShipping?.noSJ,
            counterCode: originalShipping?.destinationCode,
          },
          {
            noSJ: newShipping.noSJ,
            counterCode: newShipping.destinationCode,
            status: newShipping.status,
          }
        );
        await updateShipping(shippingId, newShipping);
        message.success("Shipping and related stocks updated.");
        history.push("/user/shipping");
        return;
      }

      // C) Only stock changed -> touch only changed rows; also update shipping (totals, etc.)
      if (stockIntrinsicChanged && !intersectChanged) {
        // deletes
        const delIds = Array.from(
          new Set([...toDeleteFlagged, ...deletedMissing].map((it) => it._id))
        );
        if (delIds.length) {
          await Promise.allSettled(delIds.map((id) => deleteStock(id)));
        }
        // upserts only changed rows
        const upserts = [
          ...added.map((cur) => buildPayload(cur)),
          ...modified.map(({ cur }) => buildPayload(cur)),
        ];
        if (upserts.length) {
          await Promise.allSettled(
            upserts.map((p) => (p._id ? updateStock(p._id, p) : createStock(p)))
          );
        }
        if (shippingOtherChanged || true /* totals changed by definition */) {
          await updateShipping(shippingId, newShipping);
        }
        message.success("Stocks updated.");
        history.push("/user/shipping");
        return;
      }

      // D) Both intersect + stock changed
      {
        // First patch intersect across all matching previous docs
        await patchIntersectStocks(
          {
            noSJ: originalShipping?.noSJ,
            counterCode: originalShipping?.destinationCode,
          },
          {
            noSJ: newShipping.noSJ,
            counterCode: newShipping.destinationCode,
            status: newShipping.status,
          }
        );

        // Then apply per-row diffs
        const delIds = Array.from(
          new Set([...toDeleteFlagged, ...deletedMissing].map((it) => it._id))
        );
        if (delIds.length) {
          await Promise.allSettled(delIds.map((id) => deleteStock(id)));
        }

        const upserts = [
          ...added.map((cur) => buildPayload(cur)),
          ...modified.map(({ cur }) => buildPayload(cur)),
        ];
        if (upserts.length) {
          await Promise.allSettled(
            upserts.map((p) => (p._id ? updateStock(p._id, p) : createStock(p)))
          );
        }

        await updateShipping(shippingId, newShipping);
        message.success("Shipping and stocks updated.");
        history.push("/user/shipping");
      }
    } catch (err) {
      console.error("Error updating shipping details:", err);
      message.error("Failed to update shipping details. Please try again.");
    }
  };
  
  const displayItems = items.filter((it) => !it._deleted);

  const columns = [
    { title: "Article", dataIndex: "article", key: "article" },
    { title: "Warna", dataIndex: "color", key: "color" },
    { title: "Size", dataIndex: "size", key: "size" },
    { title: "Assort", dataIndex: "assort", key: "assort" },
    { title: "Quantity", dataIndex: "inputQuantity", key: "quantity" },
    { title: "Harga", dataIndex: "price", key: "price" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isDeleted = record?._deleted === true;
        return (
          <div style={{ opacity: isDeleted ? 0.5 : 1 }}>
            <Button
              onClick={() => handleEditItem(record)}
              style={{ marginRight: 10 }}
              disabled={isDeleted}
            >
              Edit
            </Button>
            <Button
              onClick={() => handleDeleteItem(record)}
              danger
              disabled={isDeleted}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];





  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <h2>Edit Shipping</h2>
      <Form.Item label="Consignment Type" required>
        <Radio.Group
          onChange={(e) => setConsignmentType(e.target.value)}
          value={consignmentType}
          style={{ marginBottom: 16 }}
          buttonStyle="solid"
        >
          <Radio.Button value="consignment">Consignment</Radio.Button>
          <Radio.Button value="putus">Beli Putus</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <div style={{ width: "80vw" }}>
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="No Form" name="noForm">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="No Surat Jalan"
                name="noSJ"
                rules={[
                  { required: true, message: "Please input Shipping Number!" },
                ]}
              >
                {/* keep as plain Input so leading zeros stay intact */}
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Kirim dari"
                name="sender"
                initialValue={userData.counterCode}
                rules={[
                  { required: true, message: "Please select a Counter!" },
                ]}
              >
                <Select disabled>
                  {counterData
                    .filter((c) => c.code === userData.counterCode)
                    .map((c) => (
                      <Select.Option key={c.code} value={c.code}>
                        {c.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Kirim ke"
                name="destination"
                rules={[
                  { required: true, message: "Please select a Counter!" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a Counter"
                  onChange={(value, option) => {
                    form.setFieldsValue({ destinationCode: option.code });
                  }}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {counterData
                    .filter((c) => c.code !== userData.counterCode)
                    .map((c) => (
                      <Select.Option key={c.code} value={c.code} code={c.code}>
                        {c.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Form.Item name="destinationCode" hidden>
              <Input />
            </Form.Item>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Keterangan" name="keterangan">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Tanggal Kirim"
                name="tanggal_kirim"
                rules={[
                  { required: true, message: "Please select Tanggal Kirim!" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Tanggal Terima ETA"
                name="tanggal_terima_eta"
                rules={[
                  {
                    required: true,
                    message: "Please select Tanggal Terima ETA!",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Total Value" name="totalCost">
                <Input prefix="Rp." disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Total Pasang" name="totalQuantity">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={displayItems}
            rowKey={(record) => getItemKey(record)} // use helper here
            pagination={false}
            loading={loading || loadingStock}
          />

          <Button 
            type="primary" 
            onClick={handleAddItem}
            disabled={!Array.isArray(ArticleData) || ArticleData.length === 0}
            title={!Array.isArray(ArticleData) || ArticleData.length === 0 ? "Article data not available" : "Add new item"}
          >
            Add Item
          </Button>
          <Button type="primary" htmlType="submit" style={{ marginLeft: 10 }}>
            Update
          </Button>
          <Button
            onClick={() => history.push("/user/shipping")}
            style={{ marginLeft: 10 }}
          >
            Cancel
          </Button>
        </Form>

        <Modal
          title={editingItemIndex === null ? "Add Item" : "Edit Item"}
          open={isModalVisible}
          onOk={handleAddOrEditItem}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingItemIndex(null);
            modalForm.resetFields();
          }}
          okText={editingItemIndex === null ? "Add" : "Update"}
          confirmLoading={loadingArticles}
        >
          {loadingArticles ? (
            <div style={{ 
              padding: 20, 
              textAlign: 'center', 
              color: '#1890ff',
              backgroundColor: '#f0f8ff',
              border: '1px solid #91d5ff',
              borderRadius: 6
            }}>
              <p>🔄 Loading article data...</p>
            </div>
          ) : !Array.isArray(ArticleData) || ArticleData.length === 0 ? (
            <div style={{ 
              padding: 20, 
              textAlign: 'center', 
              color: '#ff4d4f',
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: 6
            }}>
              <p>⚠️ Article data is not available.</p>
              <p>Please refresh the page or try again later.</p>
              {articleError && (
                <p style={{ fontSize: '12px', marginTop: 10 }}>
                  Error: {articleError.message || 'Unknown error'}
                </p>
              )}
            </div>
          ) : (
            <StockCreationForm
              modalForm={modalForm}
              ArticleData={ArticleData}
              ColorData={ColorData}
              isEditMode={editingItemIndex !== null}
              stockData={
                editingItemIndex !== null ? items[editingItemIndex] : null
              }
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ShippingDetailPage;
